import _ from 'lodash';
import React, { Component } from 'react';

import FeatureFlags from 'common/feature_flags';

import { DefaultTimelineSamplingSizes, TimelineScopes } from '../../lib/constants';
import { allowTimelineScopeCurrent, getTimelineScope } from '../../lib/measureHelpers';

import * as ReportingPeriods from '../../lib/reportingPeriods';

import ComputedMeasureChart, { ComputedMeasureChartProps } from './ComputedMeasureChart';
import TimelineScopeChooser from './TimelineScopeChooser';
import { Measure } from '../../types';

import '../measure-chart.scss';

interface State {
  lastMeasure: Measure | null;
  timelineScope: TimelineScopes;
}

export type ChartProps = ComputedMeasureChartProps;

// A high-level component to render a Performance Measures timeline chart. It is currently
// featured in the Measure landing page and in Measures embedded in Stories.
//
// All you need to use this component is a Measure object obtained from /api/measures_v1/<uid>.json
// and permission for the current user to see the Measure. The component itself will handle
// fetching data when needed.
//
// Internally, MeasureChart is a light wrapper around ComputedMeasureChart (a dumb component that
// expects all data as props). MeasureChart is also responsible for rendering the chart scope
// selector buttons (Current/All reporting periods), as changing scope involves temporarily
// modifying the Measure object and re-fetching data. Data fetching is handled by wrapping
// ComputedMeasureChart in the withComputedMeasure Higher Order Component.
export class MeasureChart extends Component<ChartProps, State> {
  // Take a look at ComputedMeasureChart props. We mostly pass
  // props straight through.
  static defaultProps = { measure: null };
  constructor(props: ChartProps) {
    super(props);

    // Remove with enable_global_filters_measures usages
    if (!FeatureFlags.valueOrDefault('enable_global_filters_measures', false)) {
      this.state = {
        lastMeasure: props.measure,
        timelineScope: getTimelineScope(props.measure)
      };
    }
  }

  UNSAFE_componentWillReceiveProps(newProps: ChartProps) {
    // The timelineScope state is ephemeral - loading a new measure should reset it to the value in
    // the measure config. To implement this, we must track the last seen measure.
    if (
      !FeatureFlags.valueOrDefault('enable_global_filters_measures', false) &&
      this.state.lastMeasure !== newProps.measure
    ) {
      this.setState({
        lastMeasure: newProps.measure,
        timelineScope: getTimelineScope(newProps.measure)
      });
    }
  }

  getTimelineScope() {
    return FeatureFlags.valueOrDefault('enable_global_filters_measures')
      ? this.props.timelineScope
      : this.state?.timelineScope;
  }

  // This method takes a measure and applies the current state.timelineScope to it.
  // Please note that this method is a noop if state.timelineScope is TimelineScopes.ALL.
  //
  // To minimize impact on the rest of the platform, we treat switching to the "current"
  // reporting period as applying these transforms on the measure:
  // 1. Reporting period changed to "open"
  // 2. Reporting period start date changed to the current reporting period's start date.
  // 3. Reporting period length changed to timelineSampling.
  getScopedMeasure = (measure: Measure) => {
    const timelineScope = this.getTimelineScope();

    if (timelineScope === TimelineScopes.ALL || !allowTimelineScopeCurrent(measure)) {
      return measure;
    }

    /* eslint @typescript-eslint/no-non-null-asserted-optional-chain: "warn" */
    const reportingPeriod = measure?.metricConfig?.reportingPeriod!;
    if (!ReportingPeriods.isConfigValid(reportingPeriod)) {
      return measure;
    }

    const period = ReportingPeriods.getLastOpenReportingPeriod(reportingPeriod);

    const scopedMeasure = _.cloneDeep(measure);

    // Limit the measure to just _this_ reporting period. We're not interested in
    // historical data.
    scopedMeasure.metricConfig!.reportingPeriod!.startDateConfig.date =
      // TODO: is this a bug?
      period!.start.format('YYYY-MM-DD');

    // Set the size to the selected interval.
    const defaultSampling = DefaultTimelineSamplingSizes[reportingPeriod.size!];
    scopedMeasure.metricConfig!.reportingPeriod!.size =
      measure?.metricConfig?.display?.timelineSampling ?? defaultSampling;

    return scopedMeasure;
  };

  render() {
    const { measure } = this.props;
    const timelineScope = this.getTimelineScope();

    const enableGlobalFiltersMeasures = FeatureFlags.valueOrDefault('enable_global_filters_measures', false);
    const scopedMeasure = measure ? this.getScopedMeasure(measure) : null;
    const classes = enableGlobalFiltersMeasures ? 'measure-chart' : 'pre-forge-measure-chart measure-chart';

    return (
      <div className={classes}>
        <ComputedMeasureChart {...this.props} measure={scopedMeasure} timelineScope={timelineScope}>
          {!enableGlobalFiltersMeasures && measure && (
            <TimelineScopeChooser
              measure={measure}
              timelineScope={timelineScope}
              setTimelineScope={(scope: TimelineScopes) => this.setState({ timelineScope: scope })}
            />
          )}
        </ComputedMeasureChart>
      </div>
    );
  }
}

export default MeasureChart;
